import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { Navigate } from 'react-router-dom';
import { Page, 
         Card, 
         TextField, 
         InlineStack, 
         Divider, 
         Select, 
         BlockStack, 
         Button, 
         Text, 
         Box,
         ResourceList, 
         ResourceItem, 
         Thumbnail, 
         PageActions, 
         Banner, 
         Checkbox, 
         Badge,
         Icon, 
         ButtonGroup,
         DescriptionList
       } from '@shopify/polaris';

import {
         DeleteIcon,
         ProductIcon,
         BarcodeIcon,
         OrderDraftIcon
       } from '@shopify/polaris-icons';
import PageFooter from '../partials/PageFooter';
import LoadingSpinner from '../partials/LoadingSpinner';
import RequiresRefreshBanner from '../partials/RequiresRefreshBanner';

import SubmittedMessage from "./SubmittedMessage";

import { locationsIndex } from '../../redux/actions/locations';
import { addLineItem, clearOrderSession, publishNewOrder, deleteLineItem } from '../../redux/actions/orderSessions';
import { scanInventory } from '../../redux/actions/inventorySessions';
import { shopSettings } from '../../redux/actions/shops';

import useShopifyCurrencyFormat from '../hooks/currencyFormat';

const OrderCreation = ({ shopUrl,
                         payload,
                         orderSession,
                         locations,
                         loadLocations,
                         loadShopSettings,
                         lineItems,
                         moneyFormat,
                         postScanInventory,
                         addAction,
                         clearSession,
                         submitOrder,
                         removeLineItem,
                         requiresRefresh }) => {
  const [locationsList, setLocationsList] = useState(null);
  const [location, setLocation] = useState(undefined);

  const [submitted, setSubmitted] = useState(false);
  const [scanTerm, setScanTerm] = useState('');
  const [changeNotes, setChangeNotes] = useState('');
  const [lineItemValues, setLineItemValues] = useState({});
  const [lineItemTotals, setLineItemTotals] = useState({});
  const [totalChangeValues, setTotalChangeValues] = useState(0);
  const [totalPriceValue, setTotalPriceValue] = useState(0.0);

  const [openOnSave, setOpenOnSave] = useState(false);
  
  const [scanFocused, setScanFocused] = useState('');

  useEffect(() => {
    loadLocations(shopUrl, payload);
    loadShopSettings(shopUrl, payload);
  }, []);

  useEffect(() => {
    if(locations.status === 'success') {

      let tempLocations = [];
      locations.locations.forEach((location) => {
        tempLocations.push({ label: location.name, value: location.id })
      })

      setLocationsList(tempLocations);
    }
  }, [locations]);

  useEffect(() => {  
    if(orderSession.status === "success") {
      lineItems.map((lineItem) => {
        setValue(lineItemValues[lineItem[0]?.node?.id] || 1, lineItem);
      });
    }

    if (orderSession.status === "waiting" && !orderSession.errors) {
      setLineItemValues({});
      setLineItemTotals({});
      setTotalChangeValues(0);
      setTotalPriceValue(formatPriceValue(0.0));
      setTotals();
    }
  }, [orderSession]);

  useEffect(() => {
    setTotals();
  }, [lineItemValues]);

  useEffect(() => {
    setTotals();
  }, [moneyFormat]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if(scanTerm !== ''){
        let matchingLine = lineItems.find(lineItem => { return (lineItem[0]?.node?.barcode === scanTerm || lineItem[0]?.node?.sku === scanTerm) });

        if(!matchingLine) {
          addAction(scanTerm, lineItems.length);
          postScanInventory(shopUrl, payload, scanTerm, lineItems.length, location);
        } else {
          setValue(lineItemValues[matchingLine[0].node.id] + 1, matchingLine);
        }

        setScanTerm('');
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [scanTerm, 500]);

  const publishOrderSession = () => {
    let submitCounts = [];
    // let matchingLocation = locations.locations.find((fullLocation) => fullLocation.id === location)

    lineItems.map((event, index) => {
      if(!(event.error)){
        event[0].modifyCount = lineItemValues[event[0].node.id];
        submitCounts.push(event);
      }
    })

    submitOrder(shopUrl, payload, submitCounts, location, openOnSave);
    setSubmitted(true);
  }

  const setValue = (value, lineEvent) => {
    setLineItemTotals(lineItemTotals=>({
      ...lineItemTotals,
      [lineEvent[0]?.node?.id]: ((lineEvent[0]?.node?.price || 0.0) * parseInt(value))
    }));

    setLineItemValues(lineItemValues=>({
      ...lineItemValues,
      [lineEvent[0]?.node?.id]: parseInt(value)
    }));

    // console.log(lineItemTotals);
    // console.log(lineItemValues);
  }

  const setTotals = () => {
    let tempValue = 0;
    let tempTotal = 0.0;

    for (const [key, value] of Object.entries(lineItemValues)) {
      tempValue += (parseInt(value) || 0);
    }

    for (const [key, value] of Object.entries(lineItemTotals)) {
      tempTotal += (parseFloat(value) || 0);
    }

    setTotalChangeValues(tempValue);
    setTotalPriceValue(formatPriceValue(tempTotal));
  }

  const removeItem = (lineItem, actionId) => {
    setValue(0, lineItem);
    setTotals();
    removeLineItem(actionId);
  }

  const formatPriceValue = (value) => {
    const currencyFormat = useShopifyCurrencyFormat(moneyFormat);
    return currencyFormat(value);
  }

  const resetSession = () => {
    setSubmitted(false);
    clearSession();
    setLineItemValues({});
    setLineItemTotals({});
    setTotalChangeValues(0);
    setTotalPriceValue(formatPriceValue(0.0));
  }

  const handleLocationSelect = (event) => {
    clearSession();  
    let matchLocation = locations.locations.find(matchLocation => matchLocation.id === event);
    setLocation(matchLocation);
  }

  const updateScan = (event) => {
    setScanTerm(event);
  }

  const validateValue = (val, lineEvent) => {
    setValue(val, lineEvent);
  }

  return(
    <Page
      title="Create Order"
      primaryAction={
        <Button 
          variant="primary" 
          onClick={() => publishOrderSession()}
          disabled={!totalChangeValues || submitted}
          icon={OrderDraftIcon}
        >
            Submit Order
        </Button>
      }
      secondaryActions={
        <>
          <InlineStack align="end">
            <ButtonGroup>
              <Checkbox
                label="Open and Complete Draft Order"
                checked={openOnSave}
                disabled={submitted}
                onChange={() => setOpenOnSave(!openOnSave)}
              />
              <Button icon={DeleteIcon} variant="secondary" tone="critical" onClick={() => resetSession()}>Clear Session</Button>
            </ButtonGroup>
          </InlineStack>
        </>
      }
      backAction={{content: 'Back', url: 'https://' + window.location.hostname + window.location.search}}
    >
      { requiresRefresh &&
        <RequiresRefreshBanner />
      }
      <Card>
        <DescriptionList
          items={[
            {
              term: 'Locations',
              description:
                <>
                  { locations.status === 'success' &&
                    <Select
                      options={locationsList}
                      onChange={handleLocationSelect}
                      value={location?.id}
                      disabled={submitted}
                      placeholder="Select location"
                    />
                  }
                  { locations.status !== 'success' &&
                    <LoadingSpinner size="small" statusText=" Loading locations..." />
                  }
                </>
            },
            {
              term: 'Scan Barcode (SKU, UPC, EAN)',
              description: <TextField
                value={scanTerm}
                onChange={updateScan}
                autoComplete="off"
                placeholder={ scanFocused ? "Scan barcode" : "Click here to begin scanning" }
                prefix={<Badge tone={ scanFocused ? 'success' : 'critical' }>{ scanFocused ? 'Ready' : 'Not ready' }</Badge>}
                disabled={!location || submitted}
                onFocus={() => setScanFocused(true)}
                onBlur={() => setScanFocused(false)}
                connectedLeft={
                  <Icon
                    source={BarcodeIcon}
                    tone="base"
                  />
                }
              />
            }
          ]}
        />
      </Card>

      <Box paddingBlockStart="200">
        <Card>
          { submitted && 
            <SubmittedMessage
              shopUrl={shopUrl}
              payload={payload}
              resetSession={resetSession}
            />
          }

          <Box padding="400">
            { moneyFormat &&
              <Text>
                Subtotal: {totalPriceValue}
              </Text>
            }
          </Box>

          <Divider />

          <Box padding="400">
            <ResourceList
              resourceName={{singular: 'count', plural: 'counts'}}
              items={lineItems.toReversed()}
              renderItem={(item) => {
                const media = <Thumbnail size="small" source={item[0]?.node?.product?.media?.edges[0]?.node?.preview?.image?.url || ProductIcon} alt={item[0]?.node?.product?.media?.edges[0]?.node?.preview?.image?.altText} />;

                if(item.error) {
                  return (
                    <ResourceItem
                      id={item.actionId}
                    >
                      <Text tone="critical">
                        <strong>Error Description</strong>: {item.error}
                      </Text>
                      <Button 
                        variant="plain"
                        tone="critical"
                        icon={DeleteIcon}
                        disabled={submitted}
                        onClick={() => removeItem(item, item.actionId)}
                      >
                        Remove Product
                      </Button>
                    </ResourceItem>
                  )
                }

                if(item.placeholder) {
                  return(
                    <ResourceItem
                      id={item.actionId}
                    >
                      <LoadingSpinner size="small" statusText=" Finding product..." />
                      <Text>
                        Scanned value: {item.sku}
                      </Text>
                    </ResourceItem>
                  )
                }

                return (
                  <ResourceItem
                    id={item.actionId}
                    media={media}
                    accessibilityLabel={`Line item details for ${item[0]?.node?.displayName}`}
                  >
                    <Text variant="bodyMd" fontWeight="bold" as="h3">
                      {item[0]?.node?.displayName}
                    </Text>

                    <Button 
                      variant="plain"
                      tone="critical"
                      icon={DeleteIcon}
                      disabled={submitted}
                      onClick={() => removeItem(item, item.actionId)}
                    >
                      Remove Product
                    </Button>

                    { item[0]?.node?.selectedOptions && item[0]?.node?.selectedOptions.map(option =>
                        <div key={option.name}>
                          <Badge 
                            tone="info"
                            key={option.name}
                          >
                            <Text>{option.value}</Text>
                          </Badge>
                           &nbsp;
                        </div>
                      )
                    }
                    <Text>
                      Inventory: {item[0]?.node?.inventoryItem?.inventoryLevel?.quantities[0]?.quantity || 0}
                    </Text>
                    <Text>
                      List Price: {formatPriceValue(item[0]?.node?.price)}
                    </Text>

                    { ((item[0]?.node?.inventoryItem?.inventoryLevel?.quantities[0]?.quantity || 0) < (lineItemValues[item[0]?.node?.id] || 0)) &&
                      <Banner
                        title="Selected location may not have enough inventory to fulfill this order."
                        tone="warning"
                      >
                      </Banner>
                    }

                    { !submitted && 
                      <div style={{ 'maxWidth': '50%' }}>
                        <TextField
                          label="Order Quantity"
                          onChange={(event) => validateValue(event, item)}
                          placeholder="Modify Quantity"
                          type="number"
                          selectTextOnFocus
                          value={lineItemValues[item[0]?.node?.id] || ''}
                        />
                      </div>
                    }
                    { submitted &&
                      <div style={{ 'maxWidth': '50%' }}>
                        <TextField
                          label="Order Quantity"
                          placeholder="Modify Quantity"
                          type="number"
                          value={lineItemValues[item[0]?.node?.id] || ''}
                          disabled
                        />
                      </div>
                    }
                  </ResourceItem>
                );
              }}
            >
            </ResourceList>
          </Box>
        </Card>
      </Box>

      <PageActions
        primaryAction={
          <Button 
            variant="primary" 
            onClick={() => publishOrderSession()}
            disabled={!totalChangeValues || submitted}
            icon={OrderDraftIcon}
          >
              Submit Order
          </Button>
        }
        secondaryActions={
          <>
            <InlineStack align="end">
              <ButtonGroup>
                <Checkbox
                  label="Open and Complete Draft Order"
                  checked={openOnSave}
                  disabled={submitted}
                  onChange={() => setOpenOnSave(!openOnSave)}
                />
                <Button icon={DeleteIcon} variant="secondary" tone="critical" onClick={() => resetSession()}>Clear Session</Button>
              </ButtonGroup>
            </InlineStack>
          </>
        }
      />

      <PageFooter stripeBackground='#39a0a0' helpLink="inventory-management/create-orders-with-shopify/" helpDescription="creating orders" />
    </Page>
  )
}

const mapStateToProps = state => ({
  requiresRefresh: state.activeToken.requiresRefresh,
  locations: state.locations,
  moneyFormat: state?.shops?.shopSettings?.shop?.currencyFormats?.moneyFormat,
  orderSession: state.orderSessions,
  lineItems: state.orderSessions.lineItems,
});

const mapDispatchToProps = (dispatch) => ({
  loadLocations: (shopUrl, payload) => dispatch(locationsIndex(shopUrl, payload)),
  loadShopSettings: (shopUrl, payload) => dispatch(shopSettings(shopUrl, payload)),
  postScanInventory: (shopUrl, payload, barcode, actionId, activeLocation) => dispatch(scanInventory(shopUrl, payload, barcode, actionId, activeLocation)),
  addAction: (barcode, actionId) => dispatch(addLineItem(barcode, actionId)),
  clearSession: () => dispatch(clearOrderSession()),
  submitOrder: (shopUrl, payload, actions, activeLocation, openOnSave) => dispatch(publishNewOrder(shopUrl, payload, actions, activeLocation, 'complete', openOnSave)),
  submitDraftOrder: (shopUrl, payload, actions, activeLocation) => dispatch(publishNewOrder(shopUrl, payload, actions, activeLocation, 'draft')),
  removeLineItem: (actionId) => dispatch(deleteLineItem(actionId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderCreation);
